import qs from "querystring";
import { useEffect, useState } from "react";
import { Box, Flex, Icon } from "@storyofams/react-ui";
import axios from "axios";
import { transformId } from "~context";
import { Star } from "~components/common/Icons";
import {
  AddToCartButton,
  AddToWishlistButton,
} from "~components/product/components";
import { Text, Button, Image, Heading } from "../../../components";
import {
  formatAlgoliaProductsForTracking,
  formatShopifyIdForTracking,
  storeProductClickOrigin,
  track,
} from "../../../lib";
import { CurrencyCode } from "../../../lib/shopify/sdk";
import { shopifyToIntlPrice } from "../../../lib/shopify/utils";

export const ProductCardSmall = ({
  onClick,
  index,
  products,
  size,
  ...product
}: any) => {
  const score = product?.metafield?.value;
  const formatPrice = (product) => {
    if (+product?.priceRange?.minVariantPrice?.amount === 0) {
      if (product?.price) {
        return shopifyToIntlPrice({
          currencyCode: CurrencyCode.Eur,
          amount: product.price,
        });
      }
      if (product?.variants?.edges[0]?.node?.priceV2) {
        const variantPrice = product?.variants?.edges[0]?.node?.priceV2;
        return shopifyToIntlPrice(variantPrice);
      }
    }
    return shopifyToIntlPrice(product?.priceRange?.minVariantPrice);
  };
  const longDescription = product?.metafields
    ?.find((item) => item.key === "description_excerpt")
    ?.value?.replace(/<\/?[^>]+(>|$)/g, " ");
  return (
    <Button
      as="a"
      href={`/products/${product?.handle}`}
      variant="unstyled"
      onClick={(e) => {
        if (onClick) {
          onClick(e);
        }
        storeProductClickOrigin(
          `${product.id}` || formatShopifyIdForTracking(product.id),
          window.location.pathname
        );
        track({
          event: "dl_select_item",
          ecommerce: {
            currencyCode: "EUR",
            click: {
              actionField: { list: window.location.pathname },
              products: product.objectID
                ? formatAlgoliaProductsForTracking({
                    hits: [product],
                  })
                : [
                    {
                      name: product.title,
                      id: product.variants?.edges?.[0]?.node?.sku || "",
                      product_id: formatShopifyIdForTracking(product.id),
                      variant_id:
                        formatShopifyIdForTracking(
                          product.variants?.edges?.[0]?.node?.id
                        ) || "",
                      price: product?.priceRange?.minVariantPrice?.amount || "",
                      brand: product.vendor || "",
                      position: index,
                      category: product.productType || "",
                      list: window.location.pathname,
                    },
                  ],
            },
          },
        });
      }}
    >
      <Flex flexDirection={["column", size === 1 ? "row" : "column"]}>
        <Box
          mr={[0, size === 1 && "24px"]}
          height={[
            "216px",
            size === 3 ? "200px" : size === 1 ? "167px" : "300px",
          ]}
          width={["100%", size === 1 && "257px"]}
          position="relative"
          border="1px"
          flex="none"
          borderColor="grey100"
          bg="white"
          borderRadius="md"
        >
          <AddToWishlistButton
            product={product}
            border="none"
            bg="unset"
            position="absolute"
            left={0}
            bottom={0}
            zIndex={1}
          />
          <Box
            position="absolute"
            right={0}
            bottom={1}
            zIndex={1}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <AddToCartButton border="none" product={product} />
          </Box>
          <Box display="flex" justifyContent="center">
            <Box
              width={[
                "210px",
                size === 3
                  ? "190px"
                  : size === 1
                  ? "160px"
                  : size === 4
                  ? "100%"
                  : "300px",
              ]}
              height={[
                "210px",
                size === 3 ? "190px" : size === 1 ? "160px" : "300px",
              ]}
            >
              {typeof product?.images?.edges?.[0]?.node?.originalSrc ===
              "string" ? (
                <Image
                  height="100%"
                  width="100%"
                  layout="fill"
                  objectFit="contain"
                  objectPosition="center"
                  quality={100}
                  src={
                    product?.metaImage?.value
                      ? product.metaImage.value
                      : product?.images?.edges?.[0]?.node?.originalSrc
                  }
                  placeholder={
                    product?.smallImages?.edges?.[0]?.node?.originalSrc
                      ? "blur"
                      : "empty"
                  }
                  blurDataURL={
                    product?.smallImages?.edges?.[0]?.node?.originalSrc
                  }
                  alt={
                    product?.images?.edges?.[0]?.node?.altText || product?.title
                  }
                />
              ) : (
                JSON.stringify(product?.images?.edges?.[0]?.node?.originalSrc)
              )}
            </Box>
          </Box>
        </Box>
        <Box mt="8px" overflow="hidden">
          <Box textAlign="left" overflow="hidden">
            <Text
              fontSize={"16px"}
              fontWeight={700}
              lineHeight="20px"
              fontFamily={"DINPro"}
              css={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {product?.title?.toUpperCase()}
            </Text>
          </Box>
          <Box display="flex" justifyContent={"space-between"}>
            <Text
              color="grey600"
              fontWeight="normal"
              lineHeight={1.5}
              pt="2px"
              fontSize={"16px"}
              display="inline-flex"
            >
              {product?.priceRange?.minVariantPrice?.amount !==
              product?.priceRange?.maxVariantPrice?.amount
                ? "vanaf "
                : ""}
              {formatPrice(product)}
            </Text>
            {!!score && (
              <Box
                display={"flex"}
                fontWeight="bold"
                alignItems="center"
                lineHeight={1.5}
              >
                <Text fontSize={"16px"} pl={1}>
                  {score}
                </Text>
                <Icon
                  ml={0.5}
                  fontSize={"16px"}
                  color="oldPink"
                  icon={Star}
                  pb={"1px"}
                />
              </Box>
            )}
          </Box>
          {size === 1 && (
            <Box>
              {product.description ? (
                <Text
                  display={["none", "block"]}
                  overflow={"hidden"}
                  maxHeight={"75px"}
                  color="#767473"
                  fontSize={"16px"}
                  lineHeight="25px"
                >
                  {product.description}
                </Text>
              ) : (
                <Text
                  dangerouslySetInnerHTML={{ __html: longDescription }}
                  display={["none", "block"]}
                  overflow={"hidden"}
                  maxHeight={"75px"}
                  color="#767473"
                  fontSize={"16px"}
                  lineHeight="25px"
                ></Text>
              )}
            </Box>
          )}
        </Box>
      </Flex>
    </Button>
  );
};
